<template>
  <Print v-if="isPrint" :board="board" :form="form" />
  <div v-else>
    <!--기본정보-->
    <field-select-module v-model="form.stdInfo" :board="this.board" :is-print="isPrint" @selectField="changeField" />

    <!-- 제품정보 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 제품정보</h5>

      <div class="writeGridContainer">
        <div class="writeGridCol2">
          <div>제품명(물질명)</div>
          <p v-if="isPrint" class="print-input">{{ form.제품명 }}</p>
          <ml-input v-else v-model="form.제품명" />
        </div>
        <div>
          <div>MSDS 등록번호</div>
          <p v-if="isPrint" class="print-input">{{ form.msds등록번호 }}</p>
          <ml-input v-else v-model="form.msds등록번호" />
        </div>
      </div>
      <div class="writeGridContainer">
        <div>
          <div>제조사(공급사)</div>
          <p v-if="isPrint" class="print-input">{{ form.제조사 }}</p>
          <ml-input v-else v-model="form.제조사" />
        </div>
        <div>
          <div>제조사(공급사)<br />전화번호</div>
          <p v-if="isPrint" class="print-input">{{ form.제조사전화번호 }}</p>
          <ml-input v-else v-model="form.제조사전화번호" />
        </div>
        <div>
          <div>용도</div>
          <p v-if="isPrint" class="print-input">{{ form.용도 }}</p>
          <ml-input v-else v-model="form.용도" />
        </div>
      </div>
    </div>

    <!-- 경고표지 요소 -->
    <GHSWarningLabel :isReadonly="board.readonly" :isPrint="isPrint" v-model="form.경고표지요소" />

    <!-- 물질안전보건자료 목록 -->
    <MSDSTable title="구성성분 정보" :isReadonly="board.readonly" :isPrint="isPrint" v-model="form.구성성분" class="mt-28px" />

    <!-- 취급 부서 -->
    <HandlingDeptTable title="취급 부서" :isReadonly="board.readonly" :isPrint="isPrint" v-model="form.취급부서" class="mt-28px" />

    <!-- 관련 파일첨부 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i> 관련 파일첨부</h5>

      <div class="writeGridContainer">
        <div>
          <div>MSDS</div>
          <p v-if="isPrint" class="print-input">{{ form.MSDS && form.MSDS.fileName ? form.MSDS.fileName : "" }}</p>
          <file-uploader v-else :readonly="board.readonly" v-model="form.MSDS" />
        </div>
        <div>
          <div>경고표지</div>
          <p v-if="isPrint" class="print-input">{{ form.경고표지 && form.경고표지.fileName ? form.경고표지.fileName : "" }}</p>
          <file-uploader v-else :readonly="board.readonly" v-model="form.경고표지" />
        </div>
        <div>
          <div>교육날인부</div>
          <p v-if="isPrint" class="print-input">{{ form.교육날인부 && form.교육날인부.fileName ? form.교육날인부.fileName : "" }}</p>
          <file-uploader v-else :readonly="board.readonly" v-model="form.교육날인부" />
        </div>
      </div>
    </div>

    <file-upload-module title="파일첨부" :readonly="board.readonly" v-model="form.fileList" :is-print="isPrint" class="mt-28px" />

    <popup-load-prev-board v-model="showLoadPrevBoard" :set-board-id="1030" @selectRowData="prevBoardSelectRow" />
    <input ref="ocrFileInput" type="file" accept="application/pdf,.pdf" style="display: none" @change="onOcrFileSelected" />
  </div>
</template>

<script>
import DateSelector from "@/components/selector/DateSelector";
import FileUploadModule from "@/views/modules/FileUploadModule";
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import WriteGridContainer from "@/components/WriteGridContainer.vue";
import CustomOptionSelector from "@/components/selector/CustomOptionSelector.vue";
import PhotoUploadGridModule from "@/views/modules/PhotoUploadGridModule.vue";
import NameWithSignatureRow from "@/components/NameWithSignatureRow.vue";
import MSDSTable from "./MSDSTable.vue";
import GHSWarningLabel from "./GHSWarningLabel.vue";
import HandlingDeptTable from "./HandlingDeptTable.vue";
import Print from "./Print.vue";
import { parseMsdsPdf } from "./msdsPdfParser";
import { getHandlingDepts } from "./handlingDeptCompat";

import FileUploader from "@/components/FileUploader.vue";
import PopupLoadPrevBoard from "@/popup/PopupLoadPrevBoard.vue";

export default {
  components: {
    Print,
    PopupLoadPrevBoard,
    FileUploader,
    MSDSTable,
    GHSWarningLabel,
    HandlingDeptTable,
    NameWithSignatureRow,
    PhotoUploadGridModule,
    CustomOptionSelector,
    WriteGridContainer,
    DateSelector,
    FileUploadModule,
    FieldSelectModule,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  watch: {
    value: {
      important: true,
      deep: true,
      handler(value) {
        if (value) {
          const keys = Object.keys(value);
          for (const key of keys) {
            if (value[key]) {
              this.form[key] = value[key];
            }
          }
          // 개선안 이전 문서(공장명/공정명 단일값)는 취급부서 배열이 없으므로 한 행으로 변환해 표시
          if (!this.form.취급부서 || this.form.취급부서.length === 0) {
            const converted = getHandlingDepts(value);
            if (converted.length > 0) this.form.취급부서 = converted;
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);
      },
    },
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
  },
  data() {
    return {
      showLoadPrevBoard: false,
      ocrUploading: false,
      form: {
        stdInfo: {},

        공장명: "",
        공정명: "",

        제품명: "",
        msds등록번호: "",
        제조사: "",
        용도: "",

        경고표지요소: {},

        구성성분: [],
        취급부서: [],
        MSDS: {},
        경고표지: {},
        교육날인부: {},

        fileList: [],
      },
    };
  },
  methods: {
    changeField() {
      this.form.공장명 = "";
      this.form.공정명 = "";
    },
    setNormalBoardButtons(template) {
      // const 이전문서불러오기 = {
      //   shouldShow: { mode: "c|u", freePass: 0, permission: "c|u", onlyWriter: true},
      //   component: "vs-button",
      //   props: { class: "col-span-2", color: "secondary", type: "border" },
      //   content: "MSDS 관리 불러오기",
      //   emits: {
      //     click: (func) => {
      //       this.showLoadPrevBoard = true;
      //     },
      //   },
      // }
      const MSDS관리PDF업로드 = {
        shouldShow: { mode: "c|u", freePass: 0, permission: "c|u", onlyWriter: true },
        component: "vs-button",
        props: { class: "col-span-2", color: "secondary", type: "border" },
        content: "MSDS관리 PDF 업로드(OCR)",
        emits: {
          click: () => {
            // setNormalBoardButtons 는 초기 마운트 시 한 번만 호출돼 버튼 설정이 고정되므로,
            // 파싱 중 라벨/비활성화를 반응형으로 바꾸는 대신 재진입만 막는다.
            if (this.ocrUploading) return;
            this.$refs.ocrFileInput.click();
          },
        },
      };
      return [MSDS관리PDF업로드, template.print, template.delete, template.move, template.save];
    },
    prevBoardSelectRow(data) {
      let postContent = data.post_content;
      if (postContent) {
        postContent = JSON.parse(postContent);
        const body = postContent.body;

        body.취급부서 = getHandlingDepts(body);
        this.form = body;
      }
    },
    async onOcrFileSelected(evt) {
      const file = evt.target.files && evt.target.files[0];
      evt.target.value = ""; // 같은 파일을 다시 선택해도 change 이벤트가 나도록 초기화
      if (!file) return;

      this.ocrUploading = true;
      try {
        const parsed = await parseMsdsPdf(file);
        this.form = { ...this.form, ...parsed };

        const found =
          [parsed.제품명, parsed.제조사, parsed.용도].filter(Boolean).length +
          parsed.구성성분.length +
          parsed.경고표지요소.유해위험문구.length +
          parsed.경고표지요소.예방조치문구.length +
          Object.values(parsed.경고표지요소.그림문자 || {}).filter(Boolean).length;
        if (found === 0) {
          this.$vs.notify({ title: "알림", text: "PDF에서 인식된 항목이 없습니다. 텍스트를 선택할 수 없는(스캔 이미지) 파일이거나 서식이 달라 자동인식이 어려울 수 있습니다. 값을 직접 입력해주세요.", color: "warning" });
        } else {
          this.$vs.notify({ title: "알림", text: "PDF에서 인식된 내용을 반영했습니다. 값을 확인 후 저장해주세요.", color: "success" });
        }
      } catch (err) {
        this.$vs.notify({ title: "PDF 인식 실패", text: (err && err.message) || "PDF에서 데이터를 자동으로 인식하지 못했습니다. 값을 직접 입력해주세요.", color: "danger" });
      } finally {
        this.ocrUploading = false;
      }
    },
  },
};
</script>
